import { Box, Button, InputLabel, Stack, Typography } from "@mui/material";
import ModalContainer from "../UIElements/modal-container";
import { useTheme } from "@mui/material/styles";
import { InputCustomSize, TextArea } from "../UIElements/input-ui";
import { labelSty } from "../UIElements/styles-input";
import { useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNotificationService } from "../notification";
import { apiScheduleInterview } from "../../api/schedules";
import { LoadingButton } from "@mui/lab";
import CustomDateTimePicker from "../UIElements/date-time-picker";
import SelectInput from "../UIElements/select";

const RejectCandidateModal = ({ open, modalHandler, rejectHandler }: any) => {
  const theme = useTheme();
  const [reason, setReason] = useState("");
  return (
    <ModalContainer
      sx={{
        borderRadius: "16px",
      }}
      size={"xs"}
      openModal={open}
      fullWidth={true}
      handleModal={() => {}}
    >
      <Box sx={{ background: "#fff", padding: 3, borderRadius: "16px" }}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: "Outfit",
            fontWeight: 500,
            color: "#292925",
          }}
        >
          Reject Candidate
        </Typography>
        <Box component={"form"}>
          <Stack sx={{ my: 3 }}>
            <InputLabel sx={{ ...labelSty }}>Reasons </InputLabel>
            <TextArea
              placeholder={"Enter text here."}
              value={reason}
              onchange={(e) => setReason(e.target.value)}
            />
          </Stack>
          <Stack
            direction={"row"}
            justifyContent={"space-around"}
            sx={{ gap: 3 }}
          >
            <Button
              onClick={modalHandler}
              sx={{
                width: 150,
                borderRadius: "12px",
                background: theme.palette.lightBlue.light,
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={rejectHandler}
              sx={{ width: 150, borderRadius: "12px", textTransform: "none" }}
              variant="contained"
              color="error"
            >
              Reject
            </Button>
          </Stack>
        </Box>
      </Box>
    </ModalContainer>
  );
};

export default RejectCandidateModal;

export const ScheduleInterviewModal = ({
  open,
  modalHandler,
  candidateId,
}: any) => {
  const theme = useTheme();
  const { showNotification } = useNotificationService();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    location: "",
    details: "",
    type: "physical",
  });

  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const handleSelectedDate = (newValue: Dayjs | null) => {
    if (!newValue) return;

    if (newValue.isBefore(dayjs(), "day")) {
      showNotification("Date older than today", "error");
      return;
    }

    setSelectedDate(newValue);
  };

  const mutation = useMutation({
    mutationFn: (field: any) => apiScheduleInterview(field),
    onSuccess: (data) => {
      // Handle error
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
      }
      //  Handle success
      if ("data" in data) {
        showNotification(data?.data?.message ?? "Login successful", "success");

        // reload actual data
        queryClient.invalidateQueries({ queryKey: ["applicantDetails"] });

        modalHandler();
      }
    },
    onError: (error) => {
      console.error("Process failed", error);
      // Handle error
      showNotification("Process failed", "error");
    },
  });

  const handleSchedule = () => {
    if (!formData.details || !formData.type) {
      showNotification("Kindly fill inputs", "error");
      return;
    }
    if (formData.type === "physical" && !formData.location) {
      showNotification("Kindly fill location", "error");
      return;
    }

    if (dayjs(selectedDate).isBefore(dayjs(), "day")) {
      showNotification("Date older than today", "error");
      return;
    }

    if (formData?.type === "physical") {
      mutation.mutate({
        id: candidateId,
        location: formData.location,
        interview_date: dayjs(selectedDate).toISOString(),
        details: formData.details,
        type: formData.type,
      });
    } else if (formData?.type === "virtual")
      mutation.mutate({
        id: candidateId,
        interview_date: dayjs(selectedDate).toISOString(),
        details: formData.details,
        type: formData.type,
      });
  };
  return (
    <ModalContainer
      sx={{
        borderRadius: "16px",
      }}
      size={"xs"}
      openModal={open}
      fullWidth={true}
      handleModal={() => {}}
    >
      <Box sx={{ background: "#fff", padding: 3, borderRadius: "16px" }}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: "Outfit",
            fontWeight: 500,
            color: "#292925",
          }}
        >
          Interview Candidate
        </Typography>
        <Box component={"form"}>
          <SelectInput
            label={"Meeting Type"}
            value={formData?.type ?? ""}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, type: e.target.value }))
            }
            options={[
              { label: "Physical", value: "physical" },
              { label: "Virtual", value: "virtual" },
            ]}
          />
          {formData?.type === "physical" && (
            <Stack sx={{ my: 3 }}>
              <InputLabel sx={{ ...labelSty }}>Location </InputLabel>
              <InputCustomSize
                placeholder={"Enter location"}
                value={formData.location}
                onchange={(e) =>
                  setFormData((prev) => ({ ...prev, location: e.target.value }))
                }
                type={""}
                size={"medium"}
                styles={{}}
              />
            </Stack>
          )}
          <Stack sx={{ my: 3 }}>
            <InputLabel sx={{ ...labelSty }}>Schedule Date & Time </InputLabel>
            <CustomDateTimePicker
              label="Select Date & Time"
              value={selectedDate}
              onChange={handleSelectedDate}
            />
          </Stack>
          <Stack sx={{ my: 3 }}>
            <InputLabel sx={{ ...labelSty }}>Details </InputLabel>
            <TextArea
              placeholder={"Enter text here."}
              value={formData.details}
              onchange={(e) =>
                setFormData((prev) => ({ ...prev, details: e.target.value }))
              }
            />
          </Stack>
          <Stack
            direction={"row"}
            justifyContent={"space-around"}
            sx={{ gap: 3 }}
          >
            <Button
              onClick={modalHandler}
              sx={{
                width: 150,
                borderRadius: "12px",
                background: theme.palette.lightBlue.light,
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <LoadingButton
              sx={{ width: 150, borderRadius: "12px", textTransform: "none" }}
              variant="contained"
              color="primary"
              loading={mutation.isPending}
              onClick={handleSchedule}
            >
              Schedule
            </LoadingButton>
          </Stack>
        </Box>
      </Box>
    </ModalContainer>
  );
};
